import { ChevronsLeft, ChevronsRight } from "@app/ui";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { CalendarDayIcon } from "../../lib/useDynamicFavicon.js";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore } from "../../stores/index.js";

/** The whole-sidebar collapse/expand toggle (<< when open, >> when collapsed). */
export function SidebarToggle({ open }: { open: boolean }) {
  const setSidebarOpen = useSettingsStore((s) => s.setSidebarOpen);
  const Icon = open ? ChevronsLeft : ChevronsRight;
  const label = open ? "Collapse sidebar" : "Expand sidebar";
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => void setSidebarOpen(!open)}
      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

/** Brand chip shown while the sidebar is collapsed: app icon + title + the >>
 *  expand button, animated in/out (callers wrap it in AnimatePresence) so it
 *  visually slides over from / back into the sidebar. Rendered INSIDE the
 *  calendar header's left group on calendar routes (joins the top bar — the
 *  grid below keeps the full width) and as a top row on other routes
 *  (`vertical` collapses height instead of width there). */
export function CollapsedBrand({ vertical = false, className = "" }: { vertical?: boolean; className?: string }) {
  const reduce = useReducedMotion();
  const closed = vertical ? { height: 0 } : { width: 0 };
  const open = vertical ? { height: "auto" } : { width: "auto" };
  return (
    <motion.div
      initial={{ opacity: 0, x: -24, ...closed }}
      animate={{ opacity: 1, x: 0, ...open }}
      exit={{ opacity: 0, x: -24, ...closed }}
      transition={reduce ? { duration: 0 } : springs.smooth}
      className={`flex shrink-0 items-center gap-1 overflow-hidden ${className}`}
    >
      <Link to="/today" className="flex items-center gap-0.5 whitespace-nowrap text-base font-semibold" title="YourCalendar">
        <span>Your</span>
        <CalendarDayIcon size={28} />
        <span>Calendar</span>
      </Link>
      <SidebarToggle open={false} />
    </motion.div>
  );
}

/** Drag strip on the sidebar's right border — resizes the sidebar. Width is
 *  applied optimistically per pointer-move; the store debounces the persist. */
export function SidebarResizeHandle({ onDraggingChange }: { onDraggingChange: (dragging: boolean) => void }) {
  const { t } = useI18n();
  const setSidebarWidth = useSettingsStore((s) => s.setSidebarWidth);
  const start = useRef<{ x: number; width: number } | null>(null);
  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { x: e.clientX, width: useSettingsStore.getState().sidebarWidth };
    onDraggingChange(true);
  }
  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!start.current) return;
    void setSidebarWidth(start.current.width + (e.clientX - start.current.x));
  }
  function onPointerEnd(e: ReactPointerEvent<HTMLDivElement>) {
    if (!start.current) return;
    start.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    onDraggingChange(false);
  }
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("sidebar.resize")}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      className="absolute inset-y-0 right-0 z-10 w-1.5 cursor-col-resize touch-none hover:bg-border active:bg-border"
    />
  );
}
